import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  Link,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
  UserIcon,
} from '@backstage/core-components';
import SearchIcon from '@material-ui/icons/Search';
import MenuIcon from '@material-ui/icons/Menu';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarSearchModal } from '@backstage/plugin-search';
import customLogo from '../theme/logo/EssityWhite.png';
import customLogoLight from '../theme/logo/essity_logo_small.svg';
import MyCustomLogoFull from '../theme/logo/essity_logo_white.svg';
import MyCustomLogoFullLight from '../theme/logo/essity_logo.svg';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import HomeIcon from '@material-ui/icons/Home';
import ExploreIcon from '@material-ui/icons/Explore';
import VpnKeyIcon from '@material-ui/icons/VpnKey';

import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import GroupIcon from '@material-ui/icons/People';
import CategoryIcon from '@material-ui/icons/Category';
import AddIcon from '@material-ui/icons/Add';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { usePermission } from '@backstage/plugin-permission-react';
import { policyEntityReadPermission } from '@backstage-community/plugin-rbac-common';

const useLogoIconStyles = makeStyles({
  svg: {
    width: 'auto',
    height: 35,
    marginLeft: 20,
    marginTop: 50,
    marginBottom: 50,
  },
});

const LogoIcon = () => {
  const classes = useLogoIconStyles();
  const theme = useTheme();
  return (
    <img
      src={theme.palette.type === 'dark' ? customLogo : customLogoLight}
      alt="Essity"
      className={`essity-logo-icon ${classes.svg}`}
    />
  );
};

const useLogoFullStyles = makeStyles({
  svg: {
    width: 'auto',
    height: 35,
    marginLeft: 30,
    marginTop: 50,
    marginBottom: 50,
  },
  transition: {
    transitions: 'all 0.3s ease-in-out',
  },
});

const LogoFull = () => {
  const classes = useLogoFullStyles();
  const theme = useTheme();
  return (
    <img
      src={
        theme.palette.type === 'dark' ? MyCustomLogoFull : MyCustomLogoFullLight
      }
      alt="Essity"
      className={`essity-logo-full ${classes.svg} ${classes.transition}`}
    />
  );
};

const SidebarLogo = () => {
  const { isOpen } = useSidebarOpenState();

  return (
    <div className="essity-sidebar-logo">
      <Link
        to="/"
        underline="none"
        className="essity-sidebar-logo-link"
        aria-label="Home"
      >
        {/* Note, we don not have and expand/minimize sidebar so both logos are the same for now */}
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const AdminSidebarItem = () => {
  const { loading, allowed } = usePermission({
    permission: policyEntityReadPermission,
    resourceRef: 'policy-entity:default/rbac',
  });
  if (loading || !allowed) return null;
  return <SidebarItem icon={VpnKeyIcon} to="rbac" text="Administration" />;
};

export const appModuleNav = createFrontendModule({
  pluginId: 'app',
  extensions: [
    NavContentBlueprint.make({
      params: {
        component: ({ navItems }) => {
          const nav = navItems.withComponent(item => (
            <SidebarItem
              icon={() => item.icon}
              to={item.href}
              text={item.title}
            />
          ));
          nav.take('page:home'); // Skip home
          return (
            <Sidebar>
              {/* Logo */}
              <SidebarLogo />
              {/* Search */}
              <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
                <SidebarSearchModal />
              </SidebarGroup>
              <SidebarDivider />
              <SidebarItem icon={AddIcon} to="create" text="Create..." />
              <SidebarDivider />
              <SidebarGroup label="Menu" icon={<MenuIcon />}>
                <SidebarItem icon={HomeIcon} to="/" text="Home" />
                <SidebarItem icon={ExploreIcon} to="explore" text="Explore" />
                <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
                <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
                <MyGroupsSidebarItem
                  singularTitle="My Group"
                  pluralTitle="My Groups"
                  icon={GroupIcon}
                />
                <SidebarDivider />
                <SidebarItem icon={CreateComponentIcon} text="More">
                  <SidebarSubmenu title="More Options">
                    <SidebarDivider />
                    <SidebarSubmenuItem
                      title="Groups"
                      to="catalog?filters[kind]=group"
                      icon={GroupIcon}
                    />
                    <SidebarSubmenuItem
                      title="Users"
                      icon={UserIcon}
                      to="catalog?filters[kind]=user"
                    />
                    <SidebarDivider />
                  </SidebarSubmenu>
                </SidebarItem>
                <SidebarDivider />
                <AdminSidebarItem />
                <SidebarScrollWrapper />
              </SidebarGroup>
              <SidebarSpace />
              <SidebarDivider />
              <SidebarGroup
                label="Settings"
                icon={<UserSettingsSignInAvatar />}
                to="/settings"
              >
                <SidebarSettings />
              </SidebarGroup>
            </Sidebar>
          );
        },
      },
    }),
  ],
});
